import { createSelector, createFeatureSelector } from '@ngrx/store';
import {
	personnelDetailAdapter,
	PersonnelDetailsState,
	personnelDetailFeatureKey,
} from './personnel-detail.reducer';
import { FieldWorker, FieldWorkerPayrollType } from 'src/shared/api';
import { FieldWorkerType } from 'src/shared/enums';
import { buildListMessage } from 'src/admin-app/utilities/list-filtering/list-message';

export const getPersonnelDetailState = createFeatureSelector<
	PersonnelDetailsState
>(personnelDetailFeatureKey);

const { selectEntities } = personnelDetailAdapter.getSelectors();

export const getLoadingStatus = createSelector(
	getPersonnelDetailState,
	(state: PersonnelDetailsState) => state.isLoading
);

export const getTitle = createSelector(
	getPersonnelDetailState,
	(state: PersonnelDetailsState) =>
		state.selectedPersonnelId > 0 ? 'Edit Personnel' : 'Create Personnel'
);

export const getSelectedPersonnelId = createSelector(
	getPersonnelDetailState,
	(state: PersonnelDetailsState) => state.selectedPersonnelId
);

export const getAllPersonnelEntities = createSelector(
	getPersonnelDetailState,
	selectEntities
);

export const getSelectedPersonnel = createSelector(
	getAllPersonnelEntities,
	getSelectedPersonnelId,
	(personnelEntities, personnelId) => personnelEntities[personnelId]
);

const getPayrollType = createSelector(
	getSelectedPersonnel,
	getPersonnelDetailState,
	(personnel: FieldWorker, state: PersonnelDetailsState) => {
		let payrollType = state.payrollType;
		if (!payrollType && personnel) {
			payrollType = personnel.payrollType;
		}

		return payrollType;
	}
);

const getFieldWorkerTypeId = createSelector(
	getSelectedPersonnel,
	getPersonnelDetailState,
	(personnel: FieldWorker, state: PersonnelDetailsState) => {
		let fieldWorkerTypeId = state.fieldWorkerTypeId;
		if (!fieldWorkerTypeId && personnel && personnel.type) {
			fieldWorkerTypeId = personnel.type.id;
		}

		return fieldWorkerTypeId;
	}
);

export const getIs1099 = createSelector(
	getPayrollType,
	(payrollType: FieldWorkerPayrollType) => {
		return payrollType === FieldWorkerPayrollType.As1099;
	}
);

export const getIsFieldSupport = createSelector(
	getFieldWorkerTypeId,
	(fieldWorkerTypeId: number) => {
		return fieldWorkerTypeId === FieldWorkerType.SupportTypeId;
	}
);

export const getPersonnelEntity = (id: number) =>
	createSelector(getAllPersonnelEntities, (entities) => entities[id]);

export const getCloseAfterSaving = createSelector(
	getPersonnelDetailState,
	(state: PersonnelDetailsState) => !state.shouldStayOnPage
);

export const getIsSaving = createSelector(
	getPersonnelDetailState,
	(state: PersonnelDetailsState) => state.isSaving
);

export const getAssignments = createSelector(
	getPersonnelDetailState,
	(state) => state?.assignments ?? []
);

export const getError = createSelector(
	getPersonnelDetailState,
	(state: PersonnelDetailsState) => state.error
);

export const getShouldSaveButtonShake = createSelector(
	getPersonnelDetailState,
	(state: PersonnelDetailsState) => state.error && !state.shouldStayOnPage
);

export const getHistoryListMessage = createSelector(getPersonnelDetailState, (state) =>
	buildListMessage(
		state.error,
		state.isHistoryLoading,
		state.pagedHistoryResults
	)
);

export const getIsHistoryLoading = createSelector(getPersonnelDetailState, (state) => state.isHistoryLoading);

export const getHistory = createSelector(getPersonnelDetailState, (state) =>
	state.pagedHistoryResults ? state.pagedHistoryResults.results : []
);
